// crewbit-admin/src/pages/Settings/pages/SyncIssuesPage.tsx
//
// Admin Sync issues sub-screen (B-10d, Session 117). Admin has no offline
// queue today (§AdminOfflineQueue deferred), so the hook returns an empty
// item list and this screen renders the all-synced empty state. When the
// admin queue substrate lands, the same screen shape works against a
// populated list — only the hook implementation changes.

import { useStrandedMutations, type StrandedItem } from '../../../hooks/useStrandedMutations';
import { SettingsSubPage } from '../components/SettingsSubPage';

function describeRpc(name: string | undefined): string {
  if (!name) return 'Update';
  switch (name) {
    case 'create_invoice':
      return 'Create invoice';
    case 'update_invoice':
      return 'Update invoice';
    case 'cancel_invoice':
      return 'Cancel invoice';
    case 'set_invoice_paid_manually':
    case 'record_manual_invoice_payment':
      return 'Mark invoice paid';
    case 'record_expense':
      return 'Log expense';
    case 'record_project_payment':
      return 'Record project payment';
    default:
      return name;
  }
}

function describeReason(reason: StrandedItem['strandedReason']): string {
  switch (reason) {
    case 'account_mismatch':
      return 'Different account when this synced';
    case 'business_mismatch':
      return 'Different business when this synced';
    case 'session_token_changed':
      return 'Session changed before sync';
    case 'max_retries_exhausted':
      return "Couldn't reach server";
  }
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export function SyncIssuesPage() {
  const { items, retry, discard, isLoading } = useStrandedMutations();

  const onRetry = async (item: StrandedItem) => {
    const result = await retry(item.id);
    if (!result.success) {
      const msg =
        result.reason === 'account_mismatch'
          ? `You're not signed in to the same account this came from. Switch account and try again.`
          : result.reason === 'business_mismatch'
            ? `You're not in ${item.enqueueBusinessName ?? 'that business'} right now. Switch business and try again.`
            : result.error
              ? `Retry failed: ${result.error}`
              : 'Retry failed.';
      window.alert(msg);
    }
  };

  const onDiscard = (item: StrandedItem) => {
    const ok = window.confirm(
      `Discard this change? The ${describeRpc(item.rpcName ?? item.table).toLowerCase()} action will be permanently lost. There's no undo.`,
    );
    if (ok) void discard(item.id);
  };

  return (
    <SettingsSubPage
      title="Sync issues"
      description={
        items.length === 0
          ? 'Nothing here. All your offline actions are synced.'
          : `${items.length} item${items.length === 1 ? '' : 's'} couldn't sync.`
      }
    >
      {isLoading ? (
        <div style={{ padding: 24, color: '#6B7280' }}>Loading…</div>
      ) : items.length === 0 ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ fontWeight: 600, color: '#111827' }}>{describeRpc(item.rpcName ?? item.table)}</div>
              <div style={{ fontSize: 13, color: '#6B7280' }}>
                {item.enqueueBusinessName ??
                  (item.enqueueBusinessId ? `Business ${item.enqueueBusinessId.slice(0, 6)}…` : 'Business unknown')}
                {' · '}
                {item.enqueueAccountEmail ?? 'unknown account'}
              </div>
              <div style={{ fontSize: 13, color: '#6B7280' }}>
                Enqueued {timeAgo(item.enqueueAt)} · {describeReason(item.strandedReason)}
              </div>
              {item.lastError ? (
                <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>Last error: {item.lastError}</div>
              ) : null}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => onDiscard(item)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid #E5E7EB',
                    background: '#F9FAFB',
                    cursor: 'pointer',
                  }}
                >
                  Discard
                </button>
                <button
                  onClick={() => void onRetry(item)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#2563EB',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SettingsSubPage>
  );
}
